import { useCallback } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import { acceptedFileTypesWithoutImages } from "@/app/pages/modules/ai-explorer-constants"
import { AlertCircle, Check, CloudUpload, FileText, Upload, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

type FileUploadProps = {
  files: File[]
  onAddFiles: (files: File[]) => void
  onRemoveFile: (file: File) => void
  error?: string
}

// File upload component using react-dropzone
export function FileUpload({ files, onAddFiles, onRemoveFile, error }: FileUploadProps) {
  const { t } = useTranslation()
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onAddFiles(acceptedFiles)
    },
    [onAddFiles]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypesWithoutImages,
    multiple: true,
  })

  const removeFile = (index: number) => {
    const fileToRemove = files[index]
    onRemoveFile(fileToRemove)
  }

  return (
    <div className="space-y-6">
      <div
        {...getRootProps()}
        className={cn(
          "relative cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-[color,background-color,border-color,scale] duration-200",
          isDragActive
            ? "border-primary bg-primary/5 scale-[1.02]"
            : "border-muted-foreground/25 bg-muted/10 hover:border-muted-foreground/40 hover:bg-muted/20",
          error && "border-destructive/50 bg-destructive/5"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center space-y-4">
          <div
            className={cn(
              "rounded-full p-3 transition-colors",
              isDragActive ? "bg-primary/15" : "bg-primary/10 hover:bg-primary/15"
            )}
          >
            <CloudUpload
              className={cn(
                "h-8 w-8 transition-colors",
                isDragActive ? "text-primary" : "text-primary"
              )}
            />
          </div>
          <div className="space-y-2">
            {isDragActive ? (
              <h3 className="text-primary text-lg font-semibold">
                {t("questioninput.agent_selection.drop_files_here")}
              </h3>
            ) : (
              <>
                <h3 className="text-foreground text-lg font-semibold">
                  {t("questioninput.agent_selection.drag_drop_browse")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("questioninput.agent_selection.supported_files")}
                </p>
              </>
            )}
          </div>
          {!isDragActive && files.length === 0 && (
            <div className="border-border/50 bg-background/50 flex items-center gap-2 rounded-lg border px-4 py-2">
              <Upload className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-sm font-medium">
                {t("questioninput.agent_selection.click_to_browse", {
                  defaultValue: "Click to browse",
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="text-destructive flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-foreground text-base font-semibold">
              {t("questioninput.agent_selection.uploaded_files")} ({files.length})
            </Label>
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <Check className="text-success h-3 w-3" />
              {t("questioninput.agent_selection.ready_to_upload")}
            </div>
          </div>
          <div className="grid max-h-60 gap-3 overflow-auto">
            {files.map((file, index) => {
              const fileSizeInMB = (file.size / 1024 / 1024).toFixed(2)
              const fileExtension = file.name.split(".").pop()?.toLowerCase() || ""

              return (
                <div
                  key={index}
                  className="group bg-card hover:bg-accent/30 flex items-center gap-3 rounded-lg border p-4 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <FileText className="text-primary h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-foreground truncate text-sm font-medium">{file.name}</div>
                    <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                      <span>{fileExtension.toUpperCase()}</span>
                      <span>•</span>
                      <span>{fileSizeInMB} MB</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Button
                      className="hover:bg-destructive/10 hover:text-destructive h-8 w-8 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
